import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { useEffect, useMemo, useState } from 'react'
import { fetchImageAsBase64, PDF_JPEG_ENCODING } from 'utils/imageUtils'

export type ClientImage = {
  signedUrl: string | undefined
  id: string
  imagePath: string
  imageCaption?: string
  type: string
}

export interface ClientImagesResult {
  images: ClientImage[]
  isLoading: boolean
}

export function useClientImages(
  metadataEntryId: string,
  formData: RautFormDataState
): ClientImagesResult {
  const [isConverting, setIsConverting] = useState(true)
  const [imagesWithBase64, setImagesWithBase64] = useState<ClientImage[]>([])

  const { fileRefs: clientImageRefs, isLoading: isLoadingFileRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'client-images/**',
    })

  const { results: clientImageUrls, isLoading: isLoadingUrls } =
    useRolodexSignedURLs(
      clientImageRefs.map((ref) => ({
        entry_id: metadataEntryId,
        path: ref.path,
      }))
    )

  const processedClientImages = useMemo(() => {
    if (!formData.clientProvidedImagesData?.images?.length) return []

    return formData.clientProvidedImagesData.images.map((image) => {
      const signedUrl = clientImageUrls.find(
        (url) => url.path === image.imagePath
      )?.signed_url

      return { ...image, signedUrl }
    })
  }, [formData.clientProvidedImagesData.images, clientImageUrls])

  useEffect(() => {
    if (isLoadingFileRefs || isLoadingUrls) {
      setIsConverting(true)
      return
    }

    const imagesWithUrls = processedClientImages.filter((img) => img.signedUrl)
    if (imagesWithUrls.length === 0) {
      setImagesWithBase64([])
      setIsConverting(false)
      return
    }

    const convertImages = async () => {
      setIsConverting(true)
      try {
        const convertedImages = await Promise.all(
          imagesWithUrls.map(async (img) => {
            try {
              const base64Url = await fetchImageAsBase64(
                img.signedUrl!,
                undefined,
                undefined,
                PDF_JPEG_ENCODING
              )
              return { ...img, signedUrl: base64Url }
            } catch {
              return img
            }
          })
        )
        setImagesWithBase64(convertedImages)
      } catch {
        setImagesWithBase64(imagesWithUrls)
      } finally {
        setIsConverting(false)
      }
    }

    convertImages()
  }, [isLoadingFileRefs, isLoadingUrls, processedClientImages])

  return {
    images: imagesWithBase64,
    isLoading: isConverting || isLoadingFileRefs || isLoadingUrls,
  }
}
